/*
 * product-purchase-form.js — Variant resolution + URL push +
 * H3.0 variant:update dispatch + ATC button state machine.
 *
 * Attaches one instance per `.product-purchase-form` on the page.
 * Reads the embedded product-json blob to map selected option
 * values back to a variant; on any option change:
 *
 *   - Updates the form's hidden `id` input to the new variant id.
 *   - Pushes `?variant=<id>` to the URL (replaceState so back
 *     button still works, but no extra history entry per click).
 *   - Dispatches a `variant:update` CustomEvent with the same
 *     payload shape H3.0's native `VariantUpdateEvent` uses
 *     (`event.detail.resource = variant`). The PDP's gallery
 *     (assets/component-main-product-gallery.js) and badges
 *     (assets/product-page-badges.js) both already listen for
 *     this and react.
 *   - Resolves the ATC button state from the variant and applies
 *     it (label / class / type / disabled). Five states:
 *       atc       Add to Cart       (default, type=submit)
 *       preorder  Pre-Order         (type=submit, still buyable)
 *       oos       Out of Stock      (disabled, type=button)
 *       notify    Notify Me ...     (type=button, dispatches
 *                                    `pdp:open-notify`)
 *       enquiry   Make an Enquiry   (type=button, dispatches
 *                                    `pdp:open-enquiry`)
 *     Made-to-order metafield (custom.made_to_order) → enquiry;
 *     preorder metafield (smart.pre_order + no stock) → preorder;
 *     unavailable + no backorders → notify (if modal block
 *     present) or oos; everything else → atc.
 *
 * Initial load — if `?variant=<id>` is in the URL, that variant
 * trumps the server-rendered `selected_or_first_available_variant`
 * and the picker repaints to match (radio / select state is set
 * via the option name → input value lookup). Otherwise the
 * picker uses whatever Shopify already chose server-side. State
 * is applied on first paint either way.
 */

import { formatMoney } from '@theme/money-formatting';

const SELECTOR = '.product-purchase-form';
const INSTANCE_KEY = '_productPurchaseFormInstance';

class ProductPurchaseForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('.product-purchase-form__form');
    this.variantInput = root.querySelector('[data-product-form-variant-id]');
    this.optionInputs = Array.from(root.querySelectorAll('[data-option-select]'));
    this.atcButton = root.querySelector('[data-purchase-form-atc]');
    this.atcLabel = root.querySelector('.product-purchase-form__atc-text');

    this.productJson = this._readProductJson();
    if (!this.productJson) return;

    this.variants = this.productJson.variants || [];
    this.optionsCount = (this.productJson.options || []).length;
    this.mtoMap = this._readMtoMap();
    this.preorderMap = this._readPreorderMap();
    // Modal-presence detection runs in JS rather than server-side.
    // section.blocks is unreliable to iterate from inside both
    // rendered snippets and block files in Horizon — the
    // modal elements being present in the rendered DOM is the
    // authoritative source of truth and JS can read it directly.
    const section = root.closest('.shopify-section');
    const scope = section || document;
    this.enquiryEnabled = !!scope.querySelector('enquiry-modal-component');
    this.notifyEnabled = !!scope.querySelector('notify-me-modal-component');

    this.abortController = new AbortController();
    this._bind();
    this._bindAtcStateClicks();
    this._bindStateRequests();
    this._syncFromUrl();

    this.pillContainers = Array.from(root.querySelectorAll('.product-purchase-form__pill-container'));

    // Initial state — `_syncFromUrl` already calls `_setCurrentVariant`
    // when ?variant= is in the URL, which applies the state. Cover the
    // no-URL-param case explicitly so first paint matches the
    // selected_or_first_available_variant Shopify chose server-side.
    const initialId = this.variantInput?.value;
    if (initialId) {
      const initialVariant = this.variants.find(
        (v) => String(v.id) === String(initialId)
      );
      if (initialVariant) {
        this._applyState(this._resolveState(initialVariant), initialVariant);
      }
    }
    this._updatePillAvailability();
  }

  _readMtoMap() {
    const node = this.root.querySelector('.product-purchase-form__mto-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readPreorderMap() {
    const node = this.root.querySelector('.product-purchase-form__preorder-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readProductJson() {
    const node = this.root.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  _bind() {
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this._handleChange(), {
        signal: this.abortController.signal,
      });
    });
  }

  /**
   * Read the currently-checked / selected value for each option
   * position (1-indexed in Shopify). Returns an array indexed by
   * (position - 1) so `selected[0]` is option 1, etc.
   *
   * Custom Select dropdowns render as <custom-select-component>; the
   * actual value lives on the inner hidden input. The host element
   * itself carries the data-option-* attributes so binding stays
   * generic.
   */
  _readSelectedOptions() {
    const selected = new Array(this.optionsCount).fill(null);
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const idx = pos - 1;

      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        const hidden = el.querySelector('input[type="hidden"]');
        if (hidden) selected[idx] = hidden.value;
      } else if (el.tagName === 'SELECT') {
        selected[idx] = el.value;
      } else if (el.type === 'radio') {
        if (el.checked) selected[idx] = el.value;
      } else {
        // Defensive — handles any other input type Shopify might add later.
        if (el.value) selected[idx] = el.value;
      }
    });
    return selected;
  }

  _findVariant(selectedOptions) {
    if (!selectedOptions.every((v) => v !== null)) return null;
    return (
      this.variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return selectedOptions.every((val, i) => val === opts[i]);
      }) || null
    );
  }

  _handleChange() {
    const selected = this._readSelectedOptions();
    const variant = this._findVariant(selected);
    if (!variant) return;

    this._setCurrentVariant(variant);
    this._updatePillAvailability();
  }

  _updatePillAvailability() {
    const selected = this._readSelectedOptions();
    this.pillContainers.forEach((container) => {
      const input = container.querySelector('[data-option-select]');
      if (!input || input.type !== 'radio') return;
      const pos = parseInt(input.dataset.optionPosition, 10);
      if (!pos) return;
      const value = input.value;
      const hasAvailable = this.variants.some((v) => {
        const opts = [v.option1, v.option2, v.option3];
        if (opts[pos - 1] !== value) return false;
        return selected.every((sel, i) => i === pos - 1 || sel == null || sel === opts[i]) && v.available;
      });
      container.classList.toggle('is-soldout', !hasAvailable);
    });
  }

  _setCurrentVariant(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this._pushVariantUrl(variant.id);
    this._dispatchVariantUpdate(variant);
    this._applyState(this._resolveState(variant), variant);
  }

  /**
   * Resolve the ATC button state from a variant. Mirrors v3.0's
   * sa-product-form precedence, extended with a Pre-Order state:
   *
   *   1. Made-to-order (custom.made_to_order metafield) → 'enquiry'
   *   2. Pre-order (smart.pre_order metafield + no current stock)
   *      → 'preorder'
   *   3. Unavailable + no backorders → 'notify' (when enabled) or 'oos'
   *   4. Otherwise → 'atc'
   *
   * Pre-order check matches the v3.0 preorder-notice snippet's show
   * logic — the flag alone isn't enough; the variant also needs to be
   * out of stock (inventory_quantity <= 0). This way a merchant who
   * leaves the flag on after restocking doesn't get a "Pre-Order"
   * button on an in-stock item.
   *
   * Backorders are allowed when Shopify isn't tracking inventory
   * (inventory_management !== 'shopify') OR the policy is
   * 'continue' (sell when out of stock). When backorders are
   * allowed, an unavailable variant still resolves to 'atc' — the
   * customer can still buy.
   *
   * Modal-dependent states (enquiry, notify) only return when the
   * matching modal block is present in the section; without the
   * modal the state machine falls through to its non-modal sibling
   * (enquiry → atc; notify → oos) so the button stays usable.
   */
  _resolveState(variant) {
    if (!variant) return 'atc';

    const isMto = this.mtoMap[String(variant.id)] === true;
    if (isMto) {
      return this.enquiryEnabled ? 'enquiry' : 'atc';
    }

    const preorder = this.preorderMap[String(variant.id)];
    if (
      preorder &&
      preorder.pre_order === true &&
      (preorder.inventory_quantity == null || preorder.inventory_quantity <= 0)
    ) {
      return 'preorder';
    }

    const allowsBackorders =
      variant.inventory_management !== 'shopify' ||
      variant.inventory_policy === 'continue';

    if (variant.available || allowsBackorders) return 'atc';

    return this.notifyEnabled ? 'notify' : 'oos';
  }

  /**
   * Apply a resolved state to the ATC button: label, class
   * modifier, click target (type), and disabled. Side-effect-only;
   * the resolved state ID is the input.
   *
   * Class modifiers (.enquiry-mode / .bis-mode / .is-sold-out)
   * give the modal blocks (MP12 / MP13) a stable hook to react
   * to. The actual modal opening is dispatched via
   * `pdp:open-enquiry` / `pdp:open-notify` custom events from
   * `_bindAtcStateClicks`, scoped to the variant in detail.
   */
  _applyState(state, variant) {
    if (!this.atcButton) return;

    this.atcButton.classList.remove(
      'is-sold-out',
      'is-preorder',
      'enquiry-mode',
      'bis-mode'
    );
    this.atcButton.dataset.atcState = state;

    switch (state) {
      case 'enquiry':
        if (this.atcLabel) this.atcLabel.textContent = 'Make an Enquiry';
        this.atcButton.type = 'button';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('enquiry-mode');
        this.atcButton.dataset.variantId = variant?.id ?? '';
        break;

      case 'notify':
        if (this.atcLabel) this.atcLabel.textContent = 'Notify Me When Available';
        this.atcButton.type = 'button';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('bis-mode');
        this.atcButton.dataset.variantId = variant?.id ?? '';
        break;

      case 'oos':
        if (this.atcLabel) this.atcLabel.textContent = 'Out of Stock';
        this.atcButton.type = 'button';
        this.atcButton.disabled = true;
        this.atcButton.classList.add('is-sold-out');
        break;

      case 'preorder':
        if (this.atcLabel) this.atcLabel.textContent = 'Pre-Order';
        this.atcButton.type = 'submit';
        this.atcButton.disabled = false;
        this.atcButton.classList.add('is-preorder');
        break;

      case 'atc':
      default:
        if (this.atcLabel) this.atcLabel.textContent = 'Add to Cart';
        this.atcButton.type = 'submit';
        this.atcButton.disabled = false;
        break;
    }

    this._dispatchStateChange(state, variant);
  }

  /**
   * Broadcast the resolved state to siblings that need to mirror
   * the Purchase Form (Sticky ATC, future Quick Add variants, etc.).
   * Bubbling so a section-level listener catches it. Source of
   * truth for ATC state lives here; subscribers don't run the
   * resolver themselves.
   */
  /**
   * Late-subscriber handshake. The Purchase Form dispatches its
   * initial `pdp:state-change` during construction — before
   * later-in-source-order modules (Sticky ATC, future siblings)
   * have attached their document-level listeners. When such a
   * subscriber comes up, it fires `pdp:request-state`; we
   * re-broadcast the current resolved state so its first paint
   * matches without resorting to a setTimeout / microtask delay.
   */
  _bindStateRequests() {
    document.addEventListener(
      'pdp:request-state',
      () => {
        const id = this.variantInput?.value;
        if (!id) return;
        const variant = this.variants.find((v) => String(v.id) === String(id));
        if (!variant) return;
        this._dispatchStateChange(this._resolveState(variant), variant);
      },
      { signal: this.abortController.signal }
    );
  }

  _dispatchStateChange(state, variant) {
    this.root.dispatchEvent(
      new CustomEvent('pdp:state-change', {
        bubbles: true,
        detail: {
          state,
          variant: variant || null,
          productId: this.productJson?.id ?? null,
        },
      })
    );
  }

  /**
   * Click handlers for the enquiry / notify states. Both dispatch
   * a CustomEvent (bubbling) that the matching modal block
   * listens for; the modal pulls the variant out of detail.
   *
   * Bound once at construction and gated on the button's
   * current state class so we don't accidentally hijack ATC
   * submits.
   */
  _bindAtcStateClicks() {
    if (!this.atcButton) return;
    this.atcButton.addEventListener(
      'click',
      (event) => {
        const state = this.atcButton.dataset.atcState;
        if (state !== 'enquiry' && state !== 'notify') return;
        event.preventDefault();

        const variantId = this.variantInput?.value;
        const variant = variantId
          ? this.variants.find((v) => String(v.id) === String(variantId))
          : null;

        const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
        this.root.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            detail: {
              variant,
              productId: this.productJson?.id,
            },
          })
        );
      },
      { signal: this.abortController.signal }
    );
  }

  _pushVariantUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Dispatch a CustomEvent matching H3.0's VariantUpdateEvent
   * shape — `event.detail.resource` holds the variant. Bubbling so
   * section-level listeners (gallery, badges) catch it. Fired on
   * the root element so the listeners can scope by section via
   * `event.target.closest('.shopify-section')`.
   *
   * `data.html` is intentionally an empty Document — H3.0's
   * product-price / product-sku / product-inventory / media-
   * gallery / product-form listeners all call
   * `event.detail.data.html.querySelector(...)`; an empty
   * document satisfies the call, returns null for their
   * selectors, and they exit cleanly via their
   * `if (!result) return;` guards. We DON'T try to feed
   * H3.0's product-price a synthetic priceContainer to swap in
   * because its cached `refs.priceContainer` goes stale — the
   * MutationObserver that's meant to keep refs in sync attaches
   * via `requestIdleCallback` and misses early mutations, so
   * `priceContainer.replaceWith(newNode)` ends up operating on
   * a detached node and the live DOM never updates. Instead,
   * `_updateLivePriceBlocks` below mutates the live
   * priceContainer in place — bypassing H3.0's refs entirely.
   */
  _dispatchVariantUpdate(variant) {
    this._updateLivePriceBlocks(variant);
    this._updateAfterpay(variant);

    const event = new CustomEvent('variant:update', {
      bubbles: true,
      detail: {
        resource: variant,
        sourceId: this.root.dataset.productId || '',
        data: {
          productId: this.productJson.id,
          html: document.implementation.createHTMLDocument(''),
        },
      },
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Mutate every `<product-price>` block belonging to this
   * product directly in the live DOM — set the new price text
   * on `.price` / `.price-item--sale.price` spans, set
   * compare-at text on `.compare-at-price`, and toggle the
   * `.price__hidden` modifier on the two outer wrappers based
   * on whether `compare_at_price > price`. In-place mutation
   * (no clone-then-replace) sidesteps H3.0's refs-going-stale
   * bug — there's nothing to swap, nothing to track. Works for
   * OOS variants too; Shopify always supplies `variant.price`.
   *
   * Money format is read from `data-money-format` on the
   * Purchase Form root (Liquid picks `shop.money_with_
   * currency_format` vs `shop.money_format` based on
   * `settings.currency_code_enabled_product_pages`) so the
   * JS-built text matches the SSR pattern exactly.
   */
  _updateLivePriceBlocks(variant) {
    if (!variant) return;

    const productId = String(this.productJson?.id ?? '');
    if (!productId) return;

    const priceElements = document.querySelectorAll(
      `product-price[data-product-id="${productId}"]`
    );
    if (!priceElements.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const priceText = formatMoney(variant.price, moneyFormat, currency);
    const compareAtRaw = variant.compare_at_price;
    const compareText =
      compareAtRaw && Number(compareAtRaw) > Number(variant.price)
        ? formatMoney(compareAtRaw, moneyFormat, currency)
        : null;
    const onSale = compareText !== null;

    priceElements.forEach((pp) => {
      const regular = pp.querySelector('.price__regular');
      if (regular) {
        regular.classList.toggle('price__hidden', onSale);
        const regSpan = regular.querySelector('.price');
        if (regSpan) regSpan.textContent = priceText;
      }

      const sale = pp.querySelector('.price__sale');
      if (sale) {
        sale.classList.toggle('price__hidden', !onSale);
        const salePrice = sale.querySelector('.price-item--sale.price');
        if (salePrice) salePrice.textContent = priceText;
        const compareAtSpan = sale.querySelector('.compare-at-price');
        if (compareAtSpan && compareText) compareAtSpan.textContent = compareText;
      }
    });
  }

  /**
   * Update the Afterpay installment price (`4x $X with`) on variant
   * change. The afterpay snippet renders the price inside
   * `[data-afterpay-price]` and the rest of the copy as siblings,
   * so we only swap the price text and never touch "4x" / "with" /
   * the icon. Scoped to the section so multiple PDPs on a page
   * don't bleed.
   *
   * Mirrors the Liquid filter chain: `price | divided_by: 4 |
   * money_without_trailing_zeros`. We divide first (integer cents),
   * then format, then strip `.00` so the SSR-vs-JS output reads
   * identical for round-number installments (`$25` not `$25.00`).
   */
  _updateAfterpay(variant) {
    if (!variant) return;

    const section = this.root.closest('.shopify-section') || document;
    const afterpayEls = section.querySelectorAll('[data-afterpay-price]');
    if (!afterpayEls.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const installmentCents = Math.round(Number(variant.price) / 4);
    const formatted = formatMoney(installmentCents, moneyFormat, currency)
      .replace(/\.00(?=\D|$)/, '');

    afterpayEls.forEach((el) => {
      el.textContent = formatted;
    });
  }

  /**
   * URL-driven initial selection. If the page loaded with a
   * `?variant=<id>` param that doesn't match what Shopify picked
   * server-side, repaint the picker to match it. Without this the
   * shared / bookmarked URL would show option A while the form
   * submitted option B.
   */
  _syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const urlVariantId = parseInt(params.get('variant'), 10);
    if (!urlVariantId) return;

    const variant = this.variants.find((v) => v.id === urlVariantId);
    if (!variant) return;

    // Update the inputs to match the URL variant.
    const optionValues = [variant.option1, variant.option2, variant.option3];
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const target = optionValues[pos - 1];
      if (target == null) return;

      if (el.tagName === 'SELECT') {
        if (el.value !== target) el.value = target;
      } else if (el.type === 'radio') {
        el.checked = el.value === target;
      }
    });

    // Push state through so gallery + badges pick the right variant
    // on initial paint (they don't otherwise know about ?variant=).
    this._setCurrentVariant(variant);
  }

  destroy() {
    this.abortController.abort();
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductPurchaseForm(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
